import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Filter } from 'lucide-react';
import apiClient from '../config/axios';
import './Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [categories, setCategories] = useState(['All']);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/projects');
      const projectData = response.data.data || getDefaultProjects();
      setProjects(projectData);
      setFilteredProjects(projectData);

      const uniqueCategories = ['All', ...new Set(projectData.map(p => p.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      const defaultData = getDefaultProjects();
      setProjects(defaultData);
      setFilteredProjects(defaultData);
      setCategories(['All', ...new Set(defaultData.map(p => p.category))]);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const getDefaultProjects = () => [
    {
      id: 1,
      title: 'Weighbridge Automation',
      description: 'Developed an advanced controller device for unmanned weigh bridge operations. This solution enables automated vehicle weighing, ticket generation, and data management without manual intervention, significantly reducing operational costs and human errors.',
      category: 'Industrial Automation',
      image_url: null
    },
    {
      id: 2,
      title: 'IoT Data Bridge',
      description: 'A versatile solution that addresses devices lacking cloud or PC data transfer capabilities. Enables legacy equipment to connect to modern IoT infrastructure seamlessly, supporting multiple protocols and real-time data synchronization.',
      category: 'IoT Solutions',
      image_url: null
    },
    {
      id: 3,
      title: 'Industrial LED Controllers',
      description: 'Custom LED board solutions for rod bending machinery and automotive sun shade manufacturing. Precise control systems for industrial lighting applications with programmable patterns and intensity control.',
      category: 'LED Systems',
      image_url: null
    },
    {
      id: 4,
      title: 'Smart Metering System',
      description: 'Advanced metering infrastructure for utility companies. Features include remote reading, tamper detection, real-time consumption monitoring, and automated billing integration.',
      category: 'Smart Devices',
      image_url: null
    },
    {
      id: 5,
      title: 'Vehicle Tracking System',
      description: 'GPS-based fleet management solution with real-time tracking, geofencing, driver behavior monitoring, and comprehensive reporting capabilities for logistics companies.',
      category: 'GPS & Tracking',
      image_url: null
    },
    {
      id: 6,
      title: 'Environmental Monitoring',
      description: 'IoT-based environmental monitoring system for industrial facilities. Monitors air quality, temperature, humidity, and other critical parameters with automated alerts and compliance reporting.',
      category: 'IoT Solutions',
      image_url: null
    },
    {
      id: 7,
      title: 'Access Control System',
      description: 'Biometric and RFID-based access control system for corporate offices. Includes time attendance tracking, visitor management, and integration with HR systems.',
      category: 'Smart Devices',
      image_url: null
    },
    {
      id: 8,
      title: 'Industrial Gateway',
      description: 'Protocol conversion gateway for industrial automation. Supports Modbus, CAN, RS485, and Ethernet communication with edge computing capabilities.',
      category: 'Industrial Automation',
      image_url: null
    }
  ];

  const handleFilter = (category) => {
    setActiveFilter(category);
    if (category === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === category));
    }
  };

  return (
    <div className="projects-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Our Projects</h1>
          <p>Innovative solutions we've delivered for our clients</p>
        </div>
      </section>

      {/* Projects Section */}
      <section className="projects-section section">
        <div className="container">
          {/* Filter */}
          <div className="projects-filter">
            <Filter size={20} />
            <div className="filter-buttons">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
                  onClick={() => handleFilter(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Projects Grid */}
          <div className="projects-grid">
            {filteredProjects.map((project) => (
              <div key={project.id} className="project-card card">
                <div className="project-image">
                  <div className="image-placeholder">
                    <span>{project.title.charAt(0)}</span>
                  </div>
                  <div className="project-category-badge">{project.category}</div>
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="no-projects">
              <p>No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="projects-cta section bg-light">
        <div className="container">
          <div className="cta-content">
            <h2>Have a Project in Mind?</h2>
            <p>
              Let's discuss how we can help bring your ideas to life with our
              embedded systems expertise.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Start Your Project <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
